import { useState, useMemo, useEffect, useCallback } from 'react';
import { debounce, throttleToAnimationFrame } from '@utils/common';
import { useReRender } from '../../../hooks/useReRender.js';
import { useSelf } from '../../../hooks/useSelf.js';

// Ensure that our CHUNK_SIZE is at least the size of our viewport, such that
// initial mount can be performed without any extra logic required to avoid
// missing items in the viewport.
const ROW_CHUNK_SIZE = 10;
const INITAL_CHUNKS = 2;
const MAX_CHUNKS_RENDERED = 12;
/**
 * Hook that only renderes the minimum amount of rows to fill the current
 * viewport on mount in order to preserve performance in very long tables and
 * then continously rendering new rows when needed.
 */
function useVirtualizedRows(args) {
  const forceUpdate = useReRender();
  const [isUpdating, setIsUpdating] = useState(false);
  const chunks = useMemo(() => toChunks(args.rows, args.chunkSize), [args.chunkSize, args.rows]);
  const activeChunks = useMemo(() => {
    // HACK: We allow up to 10000 items in a single virtualized list by setting
    // activeChunks to 2000.
    // Ideally this resized dynamically when required, by we simply set it to a
    // sufficiently high value that will never be exceeded.
    const activeChunks = new Array(2000).fill(false);
    for (let i = 0; i < args.initialChunks; i++) {
      activeChunks[i] = true;
    }
    return activeChunks;
    // Ensure our active chunks are reset every time the contentKey changes as
    // this ensures that chunks will be re-rendered with newly sorted content
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [args.contentKey]);
  const chunkInsertionOrder = useMemo(() => new Array(args.initialChunks).fill(undefined).map((_, index) => index),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [activeChunks]);
  const self = useSelf({
    timeoutId: 0,
    cachedRowHeight: new Map(),
    cachedChunkHeight: new Map(),
    observers: []
  });

  // In case our itemHeight calculation changes, then we must invalidate all
  // caches as stale values may not match our new items.
  useEffect(() => {
    self.cachedChunkHeight = new Map();
    self.cachedRowHeight = new Map();
    forceUpdate();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [args.getRowHeightEstimate, args.columnCount, args.cacheKey, args.contentKey]);

  // In case the scroller height has changed, then that must mean that item
  // heights have changed, and as such we must assume previous values to be
  // stale.
  useEffect(() => {
    const table = args.tableRef.current;
    if (!table) {
      return;
    }
    const throttledUpdate = debounce(() => {
      self.cachedChunkHeight = new Map();
      self.cachedRowHeight = new Map();
      forceUpdate();
    }, 150);
    let prevWidth = undefined;
    const observer = new ResizeObserver(entries => {
      const entry = entries[0];
      if (!entry) {
        return;
      }
      const width = Math.round(entry.contentRect.width);
      if (prevWidth && width !== prevWidth) {
        throttledUpdate();
      }
      prevWidth = width;
    });
    observer.observe(table);
    return () => {
      observer.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [args.tableRef]);
  const determineHeight = useCallback(node => {
    const index = Number(node?.dataset["index"]);
    if (Number.isNaN(index) || !node) {
      return;
    }
    const height = node.getBoundingClientRect().height;
    self.cachedRowHeight.set(index, height);
    const observer = new ResizeObserver(entries => {
      const entry = entries[0];
      if (!entry || !entry.contentRect.height) {
        return;
      }
      self.cachedRowHeight.set(index, entry.contentRect.height);
    });
    observer.observe(node);
    self.observers.push(observer);
  }, [self.cachedRowHeight, self.observers]);
  const getRowHeight = useCallback((item, index) => {
    const rowHeight = self.cachedRowHeight.get(index);
    if (rowHeight) {
      return rowHeight;
    }
    if (index < 0) {
      console.trace();
    }
    return args.getRowHeightEstimate(item, index);
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [args.getRowHeightEstimate, self.cachedRowHeight]);
  const determineChunkHeight = useCallback(node => {
    const index = Number(node?.dataset["index"]);
    if (Number.isNaN(index) || self.cachedChunkHeight.has(index) || !node) {
      return;
    }
    const height = node.getBoundingClientRect().height;
    self.cachedChunkHeight.set(index, height);
    const mutationObserver = new MutationObserver(() => {
      const nextHeight = node.getBoundingClientRect().height;
      const isActive = node.dataset["active"] === "true";
      if (!nextHeight || !isActive) {
        return;
      }
      self.cachedChunkHeight.set(index, nextHeight);
    });
    const resizeObsever = new ResizeObserver(entries => {
      const entry = entries[0];
      const isActive = node.dataset["active"] === "true";
      if (!entry || !isActive || !entry.contentRect.height) {
        return;
      }
      self.cachedChunkHeight.set(index, entry.contentRect.height);
    });
    resizeObsever.observe(node);
    mutationObserver.observe(node, {
      attributeFilter: ["data-active"]
    });
    self.observers.push(resizeObsever);
    self.observers.push(mutationObserver);
  }, [self.cachedChunkHeight, self.observers]);
  const getChunkHeight = useCallback(chunkIndex => {
    const chunkHeight = self.cachedChunkHeight.get(chunkIndex);
    return chunkHeight;
  }, [self.cachedChunkHeight]);

  /**
   * Based on the current scroll position we ensure that the correct rows
   * are rendered to the DOM.
   */
  const updateChunkStates = useCallback(() => {
    const scroller = args.scroller && "current" in args.scroller ? args.scroller?.current : args.scroller;
    const boundaryHeight = args.getBoundaryHeight?.() ?? window.innerHeight;
    const startIndex = determineStartIndex(args, scroller, getRowHeight);
    const rowsToRender = determineRowsToRender(args, boundaryHeight, startIndex, getRowHeight);
    const endIndex = startIndex + rowsToRender;
    const startingChunkIndex = Math.max(0, Math.floor(startIndex / args.chunkSize) - args.extraChunksToRender);
    const endingChunkIndex = Math.min(startingChunkIndex + args.maxChunksRendered, Math.min(Math.ceil(endIndex / args.chunkSize) + args.extraChunksToRender, activeChunks.length));
    let willUpdate = false;
    for (let i = startingChunkIndex; i < endingChunkIndex; i++) {
      if (!activeChunks[i]) {
        willUpdate = true;
        activeChunks[i] = true;
        chunkInsertionOrder.push(i);

        // remove the oldest chunk from the DOM in case we've exceeded our max
        // render count in order to avoid too many DOM nodes rendered at once.
        if (chunkInsertionOrder.length > args.maxChunksRendered) {
          const oldestRenderedChunkIndex = chunkInsertionOrder.shift();
          if (typeof oldestRenderedChunkIndex === "number") {
            activeChunks[oldestRenderedChunkIndex] = false;
          }
        }
      }
    }
    if (willUpdate) {
      setIsUpdating(true);
      forceUpdate();

      // Once the virtualisation has been stable for a while, then reset
      // the isUpdating state to renable transitions etc. in consumers.
      window.clearTimeout(self.timeoutId);
      self.timeoutId = window.setTimeout(() => {
        setIsUpdating(false);
      }, 1000);
      const shouldTerminate = activeChunks.every(it => it === true);
      if (shouldTerminate) {
        if (scroller) {
          scroller.removeEventListener("scroll", updateChunkStates);
        } else {
          window.removeEventListener("scroll", updateChunkStates);
        }
      }
    }
  }, [activeChunks, args, chunkInsertionOrder, forceUpdate, getRowHeight, self]);
  useEffect(() => {
    updateChunkStates();
    return () => {
      self.observers.forEach(observer => observer.disconnect());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (args.rows.length === 0 || !args.tableRef.current || activeChunks.every(it => it === true)) {
      return;
    }
    const scroller = args.scroller && "current" in args.scroller ? args.scroller?.current : args.scroller;
    const onScroll = throttleToAnimationFrame(() => {
      updateChunkStates();
    });
    if ((scroller?.scrollTop ?? 0) > 1000) {
      updateChunkStates();
    }
    if (scroller) {
      scroller.addEventListener("scroll", onScroll);
    } else {
      window.addEventListener("scroll", onScroll);
    }
    return () => {
      if (scroller) {
        scroller.removeEventListener("scroll", onScroll);
      } else {
        window.removeEventListener("scroll", onScroll);
      }
    };
  }, [activeChunks, args.rows.length, args.scroller, args.tableRef, updateChunkStates]);
  return {
    chunks,
    activeChunks,
    isUpdating,
    getRowHeight,
    determineHeight,
    getChunkHeight,
    determineChunkHeight
  };
}
function toChunks(array, chunkSize) {
  const chunks = [];
  for (let i = 0; i < array.length; i += chunkSize) {
    chunks.push(array.slice(i, i + chunkSize));
  }
  return chunks;
}
function determineStartIndex(args, scroller, getRowHeight) {
  const scrollTop = (scroller?.scrollTop ?? window.scrollY) - args.offsetTop;
  let currentHeight = 0;
  let startIndex = 0;
  while (currentHeight < scrollTop) {
    if (args.columnCount > 1) {
      currentHeight += Math.max(...new Array(args.columnCount).fill(undefined).map((_, index) => getRowHeight(args.rows[startIndex + index], startIndex + index)));
    } else {
      currentHeight += getRowHeight(args.rows[startIndex], startIndex);
    }
    startIndex += args.columnCount;
  }
  return Math.max(startIndex, 0);
}
function determineRowsToRender(args, boundaryHeight, startIndex, getRowHeight) {
  let rowsToRender = 0;
  let currentHeight = 0;
  while (currentHeight < boundaryHeight) {
    if (args.columnCount > 1) {
      currentHeight += Math.max(...new Array(args.columnCount).fill(undefined).map((_, index) => getRowHeight(args.rows[startIndex + rowsToRender + index], startIndex + rowsToRender + index)));
    } else {
      currentHeight += getRowHeight(args.rows[startIndex + rowsToRender], startIndex + rowsToRender);
    }
    rowsToRender += args.columnCount;
  }
  return rowsToRender;
}

export { INITAL_CHUNKS, MAX_CHUNKS_RENDERED, ROW_CHUNK_SIZE, useVirtualizedRows };
//# sourceMappingURL=virtualized-rows.js.map
